// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file InternationalizationProvider.tsx
 * @input React, InternationalizationContext, i18n types
 * @output Exports InternationalizationProvider component and props type
 * @position Provider component for astryx i18n locale + messages
 *
 * Wraps a subtree with a locale and (optional) additional message catalogs +
 * overrides. Astryx components inside the subtree resolve their strings via
 * this context. If a consumer never renders a provider, astryx components
 * still work — they use the shipped en catalog directly.
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/i18n/InternationalizationContext.ts
 * - /packages/core/src/i18n/index.ts
 * - /packages/core/src/i18n/InternationalizationProvider.doc.mjs
 */
import { useMemo } from 'react';
import { InternationalizationContext } from "./InternationalizationContext.js";
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Provides locale + additional messages + overrides to all astryx
 * components in the subtree.
 */
export function InternationalizationProvider({
  locale,
  messages,
  overrides,
  children
}) {
  const value = useMemo(() => ({
    locale,
    messages: messages ?? {},
    overrides
  }), [locale, messages, overrides]);
  return /*#__PURE__*/_jsx(InternationalizationContext, {
    value: value,
    children: children
  });
}
InternationalizationProvider.displayName = 'InternationalizationProvider';